import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { Badge } from "react-bootstrap";
import toast from "react-hot-toast";
import { withAuthorizationVerifier } from "../../../components/authorization-verifier";
import { useProperty } from "../../../components/property-provider";
import { CrudListUncontrolled, TableColumns } from "../../shared/crud/CrudList";
import { StringRenderer } from "../../shared/renderers/table";
import { RendererProps } from "../../shared/renderers";
import { DefaultTableRowActionsProps } from "../../shared/actions/tableActions";
import {
  ExperimentEntity,
  ExperimentFilters as ExperimentFiltersType,
  ExperimentStatus,
} from "./domain/experiment.entity";
import { ExperimentFilters } from "./components/ExperimentFilters";
import { ScenarioVsCell } from "./components/ScenarioVsCell";
import { LiftCell } from "./components/LiftCell";
import { ExperimentRowActions } from "./components/ExperimentRowActions";
import { ExperimentGrpcRepository } from "./infrastructure/repositories/ExperimentGrpcRepository";
import { useExperiments } from "./application/hooks/useExperiments";
import { useExperimentMutations } from "./application/hooks/useExperimentMutations";
import { messages } from "./experiment.messages";
import { dateToString } from "../../../lib/dateUtils";

type ExperimentRow = ExperimentEntity & {
  activeDays: number;
  lift: number;
  hasLift: boolean;
  confidence: number;
  isReliable: boolean;
  liftTooltipKey: string;
  sampleProgress: number;
};

function DateOrDashRenderer({ value }: RendererProps<Date | null | undefined>) {
  if (!value) return <>—</>;
  return <>{dateToString(value, "dd/MM/yyyy")}</>;
}

function DurationRenderer({ value }: RendererProps<number | undefined>) {
  const intl = useIntl();
  // Only an absent value is a dash. `0` is a real, meaningful duration — a test that started
  // today — so it reads as "0 días" rather than as "we do not know".
  if (value === undefined || value === null) return <>—</>;
  return <>{intl.formatMessage(messages.common.durationDays, { days: value })}</>;
}

const experimentRepository = new ExperimentGrpcRepository();

const statusBadgeColors: Record<ExperimentStatus, "secondary" | "success" | "warning" | "info"> = {
  [ExperimentStatus.EXPERIMENT_STATUS_UNSPECIFIED]: "secondary",
  [ExperimentStatus.EXPERIMENT_STATUS_DRAFT]: "secondary",
  [ExperimentStatus.EXPERIMENT_STATUS_RUNNING]: "success",
  [ExperimentStatus.EXPERIMENT_STATUS_PAUSED]: "warning",
  [ExperimentStatus.EXPERIMENT_STATUS_ARCHIVED]: "info",
};

function getStatusLabel(status: ExperimentStatus, intl: any): string {
  switch (status) {
    case ExperimentStatus.EXPERIMENT_STATUS_UNSPECIFIED:
      return intl.formatMessage(messages.status.unspecified);
    case ExperimentStatus.EXPERIMENT_STATUS_DRAFT:
      return intl.formatMessage(messages.status.draft);
    case ExperimentStatus.EXPERIMENT_STATUS_RUNNING:
      return intl.formatMessage(messages.status.running);
    case ExperimentStatus.EXPERIMENT_STATUS_PAUSED:
      return intl.formatMessage(messages.status.paused);
    case ExperimentStatus.EXPERIMENT_STATUS_ARCHIVED:
      return intl.formatMessage(messages.status.archived);
    default:
      return intl.formatMessage(messages.status.unspecified);
  }
}

export function Experiment() {
  const history = useHistory();
  const propertyInfo = useProperty();
  const intl = useIntl();

  const [filters, setFilters] = useState<ExperimentFiltersType>({});
  const { data, isLoading, error } = useExperiments(
    propertyInfo.ticker,
    filters,
    experimentRepository,
    { enabled: !!propertyInfo.ticker }
  );
  const { softDelete } = useExperimentMutations(propertyInfo.ticker, experimentRepository);

  const rows = useMemo(
    () =>
      (data ?? []).map(item => ({
        ...item.experiment,
        activeDays: item.activeDays,
        lift: item.lift,
        hasLift: item.hasLift,
        confidence: item.confidence,
        isReliable: item.isReliable,
        liftTooltipKey: item.liftTooltipKey,
        sampleProgress: item.sampleProgress,
      })),
    [data]
  );

  const tableColumns: TableColumns<ExperimentRow> = useMemo(
    () => ({
      ticker: {
        renderer: StringRenderer,
        width: 200,
      },
      status: {
        renderer: props => (
          <Badge variant={statusBadgeColors[props.value as ExperimentStatus]}>
            {getStatusLabel(props.value as ExperimentStatus, intl)}
          </Badge>
        ),
        width: 110,
      },
      variants: {
        renderer: ScenarioVsCell,
        width: 220,
      },
      lift: {
        renderer: LiftCell,
        width: 200,
      },
      startedAt: {
        renderer: DateOrDashRenderer,
        width: 150,
      },
      estimatedEndDate: {
        renderer: DateOrDashRenderer,
        width: 150,
      },
      activeDays: {
        renderer: DurationRenderer,
        width: 110,
      },
    }),
    [intl]
  );

  const TableActions = useCallback(() => {
    return (
      <div className="flex gap-4">
        <Link className="btn btn-primary" to="experiments/new">
          <FormattedMessage {...messages.create} />
        </Link>
      </div>
    );
  }, []);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await softDelete(id);
        toast.success(intl.formatMessage(messages.rowActions.delete.success));
      } catch (e) {
        // No self-heal invalidation here: tableActions.tsx's own preConfirm/catch keeps the
        // confirmation dialog open with an inline validation message on failure, so the user
        // never leaves the confirmation flow and there is no stale-row condition to correct.
        toast.error(intl.formatMessage(messages.rowActions.delete.error));
        throw e; // rethrow so tableActions.tsx's own preConfirm/catch still runs unmodified
      }
    },
    [softDelete, intl]
  );

  const handleViewResults = useCallback(
    (id: string) => {
      history.push(`experiments/results/${id}`);
    },
    [history]
  );

  const RowActions = useMemo(
    () => ({ row, rest, onReloadList }: DefaultTableRowActionsProps<ExperimentRow>) => (
      <ExperimentRowActions
        row={row}
        rest={rest}
        onReloadList={onReloadList}
        propertyTicker={propertyInfo.ticker}
        repository={experimentRepository}
        onViewResults={handleViewResults}
        onDelete={handleDelete}
      />
    ),
    [propertyInfo.ticker, handleViewResults, handleDelete]
  );

  return (
    <CrudListUncontrolled<ExperimentRow, ExperimentFiltersType>
      items={rows}
      isLoading={isLoading}
      error={error}
      columns={tableColumns}
      actions={TableActions}
      rowActions={RowActions}
      messages={messages as any}
      hideExportButton
      hasPagination={false}
      filterBar={<ExperimentFilters value={filters} onChange={setFilters} />}
    />
  );
}

export default withAuthorizationVerifier(Experiment);
